import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  MessageCircle,
  Send,
  CheckCircle,
  Users,
  Building,
  Car,
  Wrench,
  Shield,
  HelpCircle,
  FileText,
  Search,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import goodyearHQ from '../assets/goodyear.jpg';
import houstonHQ from '../assets/houston.jpg';
import jacksonville from '../assets/jacksonville.jpg';
import losangeles from '../assets/losangeles.jpg';
import sanfrancisco from '../assets/sanfrancisco.jpg';

export default function SupportContact() {
  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Phone",
      description: "Call us for immediate assistance",
      details: "(844) 582-0750",
      action: "Call Now",
      available: "24/7 Emergency Line"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      description: "Send us a detailed message",
      details: "info@ubiquityglass.com",
      action: "Send Email",
      available: "Response within 2 hours"
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Live Chat",
      description: "Get instant help from our AI assistant",
      details: "Available 24/7",
      action: "Start Chat",
      available: "AI-powered responses"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Visit Us",
      description: "Come to our headquarters",
      details: "123 Glass Street, Houston, TX",
      action: "Get Directions",
      available: "Mon-Fri: 8AM-6PM"
    }
  ];

  const faqs = [
    {
      category: "General",
      questions: [
        {
          question: "What services does Ubiquity AutoGlass provide?",
          answer: "We provide comprehensive automotive glass services including windshield repair and replacement, side glass replacement, roof & back glass replacement, ADAS calibrations, and diagnostics & coding. All services are powered by our advanced AI technology for faster, more accurate service delivery."
        },
        {
          question: "How does your AI-powered glass identification work?",
          answer: "Our Iris technology uses advanced computer vision and machine learning to instantly identify glass damage, determine the exact part specifications needed, and automate the entire repair process. This ensures perfect fit and reduces service time significantly."
        },
        {
          question: "Do you offer mobile service?",
          answer: "Yes! We provide mobile glass repair and replacement services at your location. Our technicians come equipped with all necessary tools and equipment to complete the job on-site, saving you time and hassle."
        }
      ]
    },
    {
      category: "Services",
      questions: [
        {
          question: "How long does a windshield repair take?",
          answer: "Most windshield repairs can be completed in 30-60 minutes using our AI-powered process. The exact time depends on the extent of damage and complexity of the repair."
        },
        {
          question: "Do you work with insurance companies?",
          answer: "Yes, we work with all major insurance providers including Allstate, State Farm, GEICO, and others. We handle the insurance claim process for you to make it as seamless as possible."
        },
        {
          question: "What is ADAS calibration and why is it important?",
          answer: "ADAS (Advanced Driver Assistance Systems) calibration ensures your vehicle's safety systems like lane departure warning, automatic emergency braking, and adaptive cruise control work correctly after glass replacement. It's crucial for maintaining your vehicle's safety features."
        }
      ]
    },
    {
      category: "Technical",
      questions: [
        {
          question: "What makes your glass different from competitors?",
          answer: "We use only OEM-quality glass and our AI-powered Iris system ensures perfect identification and fit. Our technicians are certified and use advanced diagnostic tools to maintain the highest quality standards."
        },
        {
          question: "How do I know if my windshield needs repair or replacement?",
          answer: "Our AI system can assess damage through photos or in-person inspection. Generally, chips smaller than a quarter can be repaired, while larger cracks or damage in the driver's line of sight typically require replacement."
        },
        {
          question: "Do you provide warranties on your work?",
          answer: "Yes, we offer a lifetime workmanship warranty on all our services. We're so confident in our quality that we've serviced over 10,000 vehicles with zero warranty claims."
        }
      ]
    }
  ];

  const locations = [
    {
      name: "Goodyear Distribution Headquarters",
      address: "575 S 143rd Ave, Goodyear, AZ 85338",
      phone: "(844) 582-0750",
      hours: "24/7",
      image: goodyearHQ
    },
    {
      name: "Houston Distribution Hub",
      address: "3401 Navigation Blvd, Houston, TX 77003",
      phone: "(555) 123-4569",
      hours: "24/7",
      image: houstonHQ
    },
    {
      name: "Jacksonville Distribution Hub",
      address: "2619 Ignition Dr, Jacksonville, FL 32218",
      phone: "(555) 123-4570",
      hours: "24/7",
      image: jacksonville
    },
    {
      name: "Los Angeles Distribution Hub",
      address: "218 Machlin Ct, Walnut, CA 91789",
      phone: "(555) 123-4571",
      hours: "24/7",
      image: losangeles
    },
    {
      name: "San Francisco Distribution Hub",
      address: "31259 Wiegman Rd, Hayward, CA 94544",
      phone: "(555) 123-4572",
      hours: "24/7",
      image: sanfrancisco
    }
  ];

  const departments = [
    {
      icon: <Car className="w-5 h-5" />,
      title: "Customer Service",
      description: "General inquiries and service requests",
      email: "customerservice@ubiquityglass.com",
      phone: "(844) 582-0750 ext. 1"
    },
    {
      icon: <Wrench className="w-5 h-5" />,
      title: "Technical Support",
      description: "Technical issues and troubleshooting",
      email: "tech@ubiquityglass.com",
      phone: "(844) 582-0750 ext. 2"
    },
    {
      icon: <Building className="w-5 h-5" />,
      title: "Fleet Services",
      description: "Commercial and fleet account management",
      email: "fleet@ubiquityglass.com",
      phone: "(844) 582-0750 ext. 3"
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Insurance Claims",
      description: "Insurance processing and claims support",
      email: "insurance@ubiquityglass.com",
      phone: "(844) 582-0750 ext. 4"
    }
  ];

  const resources = [
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Service Manuals",
      description: "Detailed guides for our services"
    },
    {
      icon: <Wrench className="w-5 h-5" />,
      title: "Technical Documentation",
      description: "API docs and integration guides"
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Installation Guides",
      description: "Step-by-step installation instructions"
    },
    {
      icon: <AlertCircle className="w-5 h-5" />,
      title: "Safety Guidelines",
      description: "Important safety information"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-babyblue/20 border-b support-hero">
        <div className="container py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-gradient mb-6">Support & Contact</h1>
            <p className="text-lg text-gray-600 mb-8">
              Get help with your automotive glass needs. Our AI-powered support system and expert team are here to assist you 24/7.
            </p>
            <div className="flex gap-3 justify-center">
              <Button className="animated-gradient-fast subtle-glow text-white">
                <Search className="w-4 h-4 mr-2" />
                Search Help
              </Button>
              <Button variant="outline">
                <MessageCircle className="w-4 h-4 mr-2" />
                Live Chat
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-12 md:py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gradient mb-4">Get In Touch</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose the best way to reach us for your automotive glass needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-babyblue/10 rounded-lg flex items-center justify-center mb-4 text-babyblue">
                  {method.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{method.title}</h3>
                <p className="text-gray-600 mb-3">{method.description}</p>
                <p className="text-sm font-medium text-gray-900 mb-2">{method.details}</p>
                <p className="text-xs text-green-600 mb-4 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {method.available}
                </p>
                <Button variant="outline" size="sm" className="w-full">
                  {method.action}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gradient mb-4">Send Us a Message</h2>
              <p className="text-gray-600">
                Fill out the form below and we'll get back to you within 2 hours.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-babyblue focus:border-transparent"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-babyblue focus:border-transparent"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-babyblue focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-babyblue focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type
                  </label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-babyblue focus:border-transparent">
                    <option>Select a service</option>
                    <option>Windshield Repair</option>
                    <option>Windshield Replacement</option>
                    <option>Side Glass Replacement</option>
                    <option>Roof & Back Glass</option>
                    <option>ADAS Calibration</option>
                    <option>Diagnostics & Coding</option>
                    <option>Fleet Services</option>
                    <option>General Inquiry</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-babyblue focus:border-transparent"
                    placeholder="Tell us about your automotive glass needs..."
                  ></textarea>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="newsletter"
                    className="mt-1 mr-3"
                  />
                  <label htmlFor="newsletter" className="text-sm text-gray-600">
                    I would like to receive updates about Ubiquity AutoGlass services and promotions.
                  </label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full animated-gradient-fast subtle-glow text-white"
                  size="lg"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gradient mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about our services and technology.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {faqs.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: categoryIndex * 0.1 }}
                className="mb-8"
              >
                <h3 className="text-xl font-semibold mb-4 text-primary">{category.category}</h3>
                <div className="space-y-4">
                  {category.questions.map((faq, faqIndex) => (
                    <motion.div
                      key={faqIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.6, delay: faqIndex * 0.1 }}
                      className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
                    >
                      <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                      <p className="text-gray-600">{faq.answer}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Distribution Centers */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gradient mb-4">Distribution Centers</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our logistics hubs ensure rapid parts delivery and service coverage. Goodyear is our distribution headquarters; Houston serves as our distribution hub.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {locations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="h-48 bg-gray-200 relative">
                  <img
                    src={location.image}
                    alt={location.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-semibold">{location.name}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start">
                      <MapPin className="w-4 h-4 text-babyblue mt-1 mr-3 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{location.address}</span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-babyblue mr-3 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{location.phone}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-babyblue mr-3 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{location.hours}</span>
                    </div>
                  </div>
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Facility Hours</h4>
                    <div className="text-sm text-gray-600">{location.hours}</div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full">
                    Get Directions
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="py-12 md:py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gradient mb-4">Contact by Department</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Reach out to the right team for specialized assistance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {departments.map((dept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 bg-royalpurple/10 rounded-lg flex items-center justify-center mb-4 text-royalpurple">
                  {dept.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{dept.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{dept.description}</p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="w-3 h-3 mr-2" />
                    {dept.email}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-3 h-3 mr-2" />
                    {dept.phone}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gradient mb-4">Resources</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Access documentation, guides, and technical resources.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="w-10 h-10 bg-orangepeel/10 rounded-lg flex items-center justify-center mb-4 text-orangepeel group-hover:bg-orangepeel/20 transition-colors">
                  {resource.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 group-hover:text-orangepeel transition-colors">
                  {resource.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                <div className="flex items-center text-orangepeel text-sm font-medium group-hover:text-orangepeel/80 transition-colors">
                  View Resource
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Hours */}
      <section className="py-12 md:py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gradient mb-4">Support Hours</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our support team is available to help you with your automotive glass needs.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Live Chat</h3>
              <p className="text-gray-600 mb-2">24/7 Available</p>
              <p className="text-sm text-gray-500">Instant responses via AI-powered chat</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Phone Support</h3>
              <p className="text-gray-600 mb-2">Mon-Fri: 8AM-6PM CST</p>
              <p className="text-sm text-gray-500">Direct access to our expert team</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Email Support</h3>
              <p className="text-gray-600 mb-2">24/7 Response</p>
              <p className="text-sm text-gray-500">Detailed support via email</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-12 md:py-16 bg-red-50 border-t border-red-200">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-900 mb-4">Emergency Glass Repair</h2>
              <p className="text-red-700 mb-6">
                Need immediate assistance? Our emergency team is available 24/7 for urgent glass repairs and replacements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Emergency Line: (844) 582-
                </Button>
                <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Emergency Chat
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="animated-gradient particle-container subtle-glow">
        <div className="container py-12 md:py-16 text-white text-center">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="text-2xl font-bold mb-4"
          >
            Ready to Get Started?
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-6 max-w-2xl mx-auto"
          >
            Contact us today for a free estimate or to schedule your automotive glass service.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex gap-3 justify-center"
          >
            <Button className="bg-white text-secondary hover:opacity-90">
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">
              <MessageCircle className="w-4 h-4 mr-2" />
              Live Chat
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
